import type { FC } from 'react'
import { useEffect } from 'react'

import { InfoBox, LoaderCircle } from '@skytech/ui'
import { countryOptions } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import AlertIcon from 'mdi-react/AlertIcon'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import PolicyListItem from '@/components/policy/PolicyListItem'
import PolicyTile from '@/components/policy/PolicyTile'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/ecom-policy/queries'
import type { CostObjectPolicy } from '@/modules/ecom-policy/resources'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import type { Employee } from '@/resources/employees'
import { getOrderPolicyFilter } from '@/util/getPolicyFilter'

import { useShopNewOrderContext } from '../../ShopNewOrder.context'

import './styles.scss'

const StepPolicies: FC<{
  customerId?: number
  employee?: Employee
}> = ({ customerId: currentCustomerId, employee }) => {
  const profile = useProfile()
  const newShopOrderCtx = useShopNewOrderContext()
  const [costObjectId] = newShopOrderCtx.costObjectId
  const [, setActiveStepName] = newShopOrderCtx.activeStepName
  const [selectedPolicyId, setSelectedPolicyId] = newShopOrderCtx.selectedPolicyId
  const { resetOrderData } = newShopOrderCtx

  useEffect(() => {
    setActiveStepName('POLICY')
  }, [])

  const { data, isLoading } = useQuery(
    costObjectId ? queries.costObjectPolicyList(costObjectId) : [''],
    () => {
      if (!costObjectId) throw Error('Cost object id missing!')
      return ecomPolicyResources.getCostObjectPolicyList(costObjectId)
    },
    { enabled: Boolean(costObjectId) }
  )

  if (isLoading) {
    return <LoaderCircle />
  }

  if (!data) return null

  if (!data.length) {
    return (
      <NoResults>
        <Translation id="No available policies" />
      </NoResults>
    )
  }

  const extraPredicate = (policy: CostObjectPolicy) => {
    const isCrossborderOrder = profile?.customerId !== employee?.customerId
    const filterByCountryId = isCrossborderOrder ? true : policy.countryId === getCorrectCountryId()
    const filterByCustomer = Boolean(policy.isGlobal || policy.customerId === currentCustomerId)
    return filterByCountryId && filterByCustomer
  }

  const getCorrectCountryId = (): number => {
    if (employee?.isLocation) return employee.countryId
    return employee?.customerCountryId || 0
  }

  const policyOptions = getOrderPolicyFilter<CostObjectPolicy>({
    policyList: data,
    customerHasLeasing: employee?.customerHasLeasing || false,
    extraPredicate,
  })

  if (!policyOptions.length) {
    const userCountry =
      countryOptions.find(country => country.value === employee?.countryId)?.label || ''
    return (
      <div className="mx-auto w-full md:w-4/5 xl:w-3/5">
        <InfoBox emphasis="warning" marginBottom={2}>
          <AlertIcon />
          <Translation
            id="No available policies for user. Make sure to have a policy available for {userCountry}"
            userCountry={userCountry}
          />
        </InfoBox>
      </div>
    )
  }

  const isPolicyTableVisible = policyOptions.length > 12

  function handleSelectPolicy(policyId: number) {
    // When changing the policy, all related selections should be wiped
    resetOrderData(true)
    setSelectedPolicyId(policyId)
  }

  return (
    <div className={`step-policy ${isPolicyTableVisible ? 'table' : ''}`}>
      {isPolicyTableVisible ? (
        <table>
          <thead>
            <tr>
              <th />
              <th>
                <Translation id="name" />
              </th>
              <th className="is-mobile-hidden">
                <Translation id="description" />
              </th>
              <th className="is-mobile-hidden">
                <Translation id="price list" />
              </th>
              <th className="is-mobile-hidden number">
                <Translation id="covered amount" />
              </th>
            </tr>
          </thead>

          <tbody>
            {policyOptions.map(policy => (
              <PolicyListItem
                key={policy.id}
                selectedPolicyIds={selectedPolicyId ? [selectedPolicyId] : null}
                onPolicySelect={handleSelectPolicy}
                {...policy}
              />
            ))}
          </tbody>
        </table>
      ) : (
        policyOptions.map(policyListItem => (
          <PolicyTile
            key={policyListItem.id}
            selectedPolicyIds={selectedPolicyId ? [selectedPolicyId] : null}
            onPolicySelect={handleSelectPolicy}
            {...policyListItem}
          />
        ))
      )}
    </div>
  )
}

export default StepPolicies
